import { subDays } from "date-fns";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { useSegmentHistory } from "./api/useSegmentHistory";
import { HistoryEntryItem } from "./components/HistoryEntryItem";
import { SegmentHistoryToolbar } from "./components/SegmentHistoryToolbar";

import type { DateRange } from "@/components/ui/v2/date-range-picker";
import {
  HistoryTimeline,
  type EntityHistoryEntry,
} from "@/components/ui/v2/history";
import { URL_PARAMS } from "@/constants/urlParams";
import { SEGMENT_TOUR_TARGET } from "@/features/segments/tour/constants";
import { useTourTarget } from "@/features/tours/useTourTarget";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";

const DEFAULT_DAYS = 30;
const PAGE_SIZE = 30;
const CHANGE_TYPE_PARAM = "changeType";

function parseDate(value: string | null, fallback: Date): Date {
  if (!value) return fallback;
  const d = new Date(value);
  return isNaN(d.getTime()) ? fallback : d;
}

export function SegmentHistoryTab({ segmentId }: { segmentId: string }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { searchValue, debouncedSearchValue, setSearchValue } =
    useSearchSearchParams();

  const changeTypes = searchParams.getAll(CHANGE_TYPE_PARAM);
  const fromParam = searchParams.get(URL_PARAMS.DATE_FROM);
  const toParam = searchParams.get(URL_PARAMS.DATE_TO);

  const dateRange: DateRange = useMemo(() => {
    const now = new Date();
    return {
      from: parseDate(fromParam, subDays(now, DEFAULT_DAYS)),
      to: parseDate(toParam, now),
    };
  }, [fromParam, toParam]);

  const handleChangeTypesChange = useCallback(
    (types: string[]) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(CHANGE_TYPE_PARAM);
          for (const t of types) next.append(CHANGE_TYPE_PARAM, t);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleDateRangeChange = useCallback(
    (range: DateRange) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (range.from) next.set(URL_PARAMS.DATE_FROM, range.from.toISOString());
          else next.delete(URL_PARAMS.DATE_FROM);
          if (range.to) next.set(URL_PARAMS.DATE_TO, range.to.toISOString());
          else next.delete(URL_PARAMS.DATE_TO);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const historyResult = useSegmentHistory({
    segmentId,
    from: dateRange.from!,
    to: dateRange.to,
    pageSize: PAGE_SIZE,
    search: debouncedSearchValue || undefined,
    changeTypes: changeTypes.length > 0 ? changeTypes : undefined,
  });

  const newestEntry = useMemo(
    () =>
      historyResult.data?.pages
        .flatMap((page) => page.data)
        .reduce<EntityHistoryEntry | undefined>(
          (newest, entry) =>
            !newest || new Date(entry.occurredAt) > new Date(newest.occurredAt) ? entry : newest,
          undefined,
        ),
    [historyResult.data],
  );
  const newestEntryRef = useTourTarget<HTMLDivElement>(SEGMENT_TOUR_TARGET.HISTORY);
  const emptyTimelineRef = useTourTarget<HTMLDivElement>(
    SEGMENT_TOUR_TARGET.HISTORY,
    !newestEntry,
  );

  const firstPage = historyResult.data?.pages?.[0];
  const totalCount = firstPage?.totalRecords;
  const changeTypeCounts = firstPage?.changeTypeCounts ?? [];

  return (
    <div ref={emptyTimelineRef}>
      <HistoryTimeline
        useHistoryResult={historyResult}
        renderEntry={(entry: EntityHistoryEntry) =>
          entry === newestEntry ? (
            <div ref={newestEntryRef}>
              <HistoryEntryItem entry={entry} />
            </div>
          ) : (
            <HistoryEntryItem entry={entry} />
          )
        }
        i18nPrefix="admin.segments.history"
        inset="tab"
        toolbar={
          <SegmentHistoryToolbar
            search={searchValue ?? ""}
            onSearchChange={setSearchValue}
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
            changeTypes={changeTypes}
            onChangeTypesChange={handleChangeTypesChange}
            changeTypeCounts={changeTypeCounts}
            totalCount={totalCount}
          />
        }
      />
    </div>
  );
}
